import { encodeCard, encodeErrorKey, encodeRequestFromPayload } from '../../nfcagent/client.js';
import { formatApiError, isApiRequestError } from '../../utils/apiError.js';
/**
 * Write a prepared payload to the card on `readerId` and apply the write-report
 * rule: anything short of a confirmed write — or an `unknown` outcome — saves
 * nothing. Shared by `IssueCardModal` and the User UI's check-in key step so the
 * rule exists once. Transport errors are thrown, not returned.
 */
export async function writeCardImage(readerId, claimId, payload, t) {
    const encoded = await encodeCard(readerId, encodeRequestFromPayload(payload), { claimId });
    const outcomeUnknown = encoded.writeReport?.outcome === 'unknown';
    if (!outcomeUnknown && !encoded.success) {
        // A report the encoder could not MAC may follow a physical write: say so, not "failed".
        return {
            ok: false,
            message: encoded.error === 'report_mac_unavailable'
                ? t('cards.notSavedReportMac', { reason: t(encodeErrorKey(encoded.error)) })
                : t('cards.notSavedEncodingFailed', { reason: t(encodeErrorKey(encoded.error)) }),
        };
    }
    if (!encoded.writeReport || (!outcomeUnknown && encoded.writeReport.outcome !== 'written')) {
        return { ok: false, message: t('cards.writeReportMissing') };
    }
    return { ok: true, outcomeUnknown, writeReport: encoded.writeReport };
}
/** Read the exact whole-second validFrom value encoded into the signed card image. */
export function encodedValidFromIso(payloadHex) {
    // Card image bytes 8..11 are the protected uint32 LE Unix timestamp.
    if (!/^[0-9a-f]+$/i.test(payloadHex) || payloadHex.length < 24) {
        // A stable code, not English: a catch site translates it with `t()`
        // (`cards.invalidValidFromPayload`) before it reaches the screen.
        throw new Error('invalid_valid_from_payload');
    }
    const byteAt = (offset) => Number.parseInt(payloadHex.slice(offset * 2, offset * 2 + 2), 16);
    const seconds = (byteAt(8)
        + byteAt(9) * 0x100
        + byteAt(10) * 0x10000
        + byteAt(11) * 0x1000000);
    return new Date(seconds * 1000).toISOString();
}
/** The card id when the server answers that this issuance is already recorded. */
export function alreadyRecordedCardId(error) {
    const response = error?.response;
    const cardId = response?.data?.details?.cardId;
    return response?.status === 409
        && response.data?.error === 'issuance_already_recorded'
        && typeof cardId === 'string'
        && cardId.length > 0
        ? cardId
        : null;
}
/** The server refused the write report itself (422 `write_confirmation_invalid`). */
export function isWriteConfirmationInvalid(error) {
    const data = error?.response?.data;
    return data?.error === 'write_confirmation_invalid';
}
/** A translated sentence without its final full stop, to sit before the template's own ". ". */
export function withoutFinalStop(text) {
    return text.trim().replace(/\.+$/, '');
}
/**
 * What the desk reads when the save after a physical write fails. Where the
 * server refused the write report (`write_confirmation_invalid`) or the encoder
 * itself reported outcome `unknown`, nothing was confirmed written, so the
 * wording must not say "Card encoded". `retrySave` is false when the refusal
 * cannot be cured by saving again: the card has to be encoded afresh. Shared by
 * `IssueCardModal` and the User UI's key step.
 */
export function cardSaveFailure(t, error, detail, options) {
    if (isWriteConfirmationInvalid(error)) {
        return { message: t('cards.writeNotConfirmed'), retrySave: false };
    }
    const trimmed = withoutFinalStop(detail);
    if (options.outcomeUnknown) {
        return { message: t('cards.writeUnconfirmedSaveFailed', { detail: trimmed }), retrySave: true };
    }
    return {
        message: t(options.stillFailing ? 'cards.encodedSaveStillFailed' : 'cards.encodedButSaveFailed', { detail: trimmed }),
        retrySave: true,
    };
}
/**
 * The detail sentence for a failed issue, encode or save: an Edge refusal in the
 * desk's own words (never the server's English `message`), an encoder failure as
 * the agent reported it. Shared by `IssueCardModal` and the User UI's key step.
 */
export function cardErrorDetail(error, t, fallbackKey = 'cards.issuingFailedDefault') {
    if (isApiRequestError(error))
        return formatApiError(error, t, { serverMessage: false });
    if (error instanceof Error) {
        return error.message === 'invalid_valid_from_payload' ? t('cards.invalidValidFromPayload') : error.message;
    }
    return t(fallbackKey);
}
//# sourceMappingURL=writeCard.js.map